import { Raycaster, Vector2, Vector3, type Camera } from "three";

/**
 * Pure surface-fitting math extracted from the (former) SurfaceSampler class.
 * No `this`, no GPU, no state — the render-based sampler (createSurfaceSampler)
 * and the surfaceSystem feed these plain functions the readback buffer / NDC and
 * get back the geometry. Keeping the math here follows the kho taxonomy: pure
 * computation lives under editor/functions/.
 */

export type SurfaceSample = { normal: Vector3; centroid: Vector3; count: number };

/** Eigenvector of the smallest eigenvalue of a symmetric 3×3 matrix (Jacobi). */
export function smallestEigenvector(
  xx: number, xy: number, xz: number, yy: number, yz: number, zz: number,
): Vector3 | null {
  const a = [
    [xx, xy, xz],
    [xy, yy, yz],
    [xz, yz, zz],
  ];
  const v = [
    [1, 0, 0],
    [0, 1, 0],
    [0, 0, 1],
  ];
  for (let sweep = 0; sweep < 12; sweep++) {
    let off = 0;
    for (let p = 0; p < 2; p++) for (let q = p + 1; q < 3; q++) off += a[p][q] * a[p][q];
    if (off < 1e-18) break;
    for (let p = 0; p < 2; p++) {
      for (let q = p + 1; q < 3; q++) {
        if (Math.abs(a[p][q]) < 1e-20) continue;
        const theta = (a[q][q] - a[p][p]) / (2 * a[p][q]);
        const t = Math.sign(theta) / (Math.abs(theta) + Math.sqrt(theta * theta + 1));
        const c = 1 / Math.sqrt(t * t + 1);
        const s = t * c;
        for (let k = 0; k < 3; k++) {
          const akp = a[k][p], akq = a[k][q];
          a[k][p] = c * akp - s * akq;
          a[k][q] = s * akp + c * akq;
        }
        for (let k = 0; k < 3; k++) {
          const apk = a[p][k], aqk = a[q][k];
          a[p][k] = c * apk - s * aqk;
          a[q][k] = s * apk + c * aqk;
          const vkp = v[k][p], vkq = v[k][q];
          v[k][p] = c * vkp - s * vkq;
          v[k][q] = s * vkp + c * vkq;
        }
      }
    }
  }
  let min = 0;
  if (a[1][1] < a[min][min]) min = 1;
  if (a[2][2] < a[min][min]) min = 2;
  const out = new Vector3(v[0][min], v[1][min], v[2][min]);
  return out.lengthSq() > 1e-12 ? out.normalize() : null;
}

/**
 * PCA surface fit over a rendered RGBA float readback buffer. Each covered pixel
 * (alpha === 1) stores the point's position RELATIVE to `anchor` scaled by
 * 1/radius in rgb. High-frequency depth outliers are rejected by a median±MAD
 * filter along `viewDir`, then the covariance eigenvector of the smallest
 * eigenvalue gives the surface normal (flipped to face the camera) and the
 * survivors' mean gives the centroid (mapped back to world coords). Returns null
 * when too few points survive. Behaviour identical to the old sampler's inline math.
 *
 * @param buf         RGBA float pixels (length ≥ pixelCount*4)
 * @param pixelCount  number of pixels to scan (SIZE*SIZE)
 * @param viewDir     normalized ray direction (camera → scene)
 * @param anchor      the pick point the relative positions are measured from
 * @param radius      sampling radius R the relative positions were scaled by
 */
export function fitSurfaceSample(
  buf: Float32Array,
  pixelCount: number,
  viewDir: Vector3,
  anchor: Vector3,
  radius: number,
): SurfaceSample | null {
  const xs: number[] = [];
  const depths: number[] = [];
  for (let i = 0; i < pixelCount; i++) {
    if (buf[i * 4 + 3] === 0) continue;
    const x = buf[i * 4];
    const y = buf[i * 4 + 1];
    const z = buf[i * 4 + 2];
    xs.push(x, y, z);
    depths.push(x * viewDir.x + y * viewDir.y + z * viewDir.z);
  }
  const n0 = depths.length;
  if (n0 < 6) return null;

  const sorted = [...depths].sort((a, b) => a - b);
  const median = sorted[n0 >> 1];
  const devs = depths.map((d) => Math.abs(d - median)).sort((a, b) => a - b);
  const mad = devs[n0 >> 1];
  const tol = Math.max(3 * mad, 0.02);

  let cx = 0, cy = 0, cz = 0, kept = 0;
  const keep: number[] = [];
  for (let i = 0; i < n0; i++) {
    if (Math.abs(depths[i] - median) > tol) continue;
    keep.push(i);
    cx += xs[i * 3];
    cy += xs[i * 3 + 1];
    cz += xs[i * 3 + 2];
    kept++;
  }
  if (kept < 6) return null;
  cx /= kept; cy /= kept; cz /= kept;

  let xx = 0, xy = 0, xz = 0, yy = 0, yz = 0, zz = 0;
  for (const i of keep) {
    const dx = xs[i * 3] - cx;
    const dy = xs[i * 3 + 1] - cy;
    const dz = xs[i * 3 + 2] - cz;
    xx += dx * dx; xy += dx * dy; xz += dx * dz;
    yy += dy * dy; yz += dy * dz; zz += dz * dz;
  }
  const normal = smallestEigenvector(xx, xy, xz, yy, yz, zz);
  if (!normal) return null;
  if (normal.dot(viewDir) > 0) normal.negate();

  const centroid = new Vector3(cx, cy, cz).multiplyScalar(radius).add(anchor);
  return { normal, centroid, count: kept };
}

/** Build a Raycaster from a camera and NDC (-1..1) screen coords. Pure. */
export function rayFromNdc(camera: Camera, ndcX: number, ndcY: number): Raycaster {
  const raycaster = new Raycaster();
  raycaster.setFromCamera(new Vector2(ndcX, ndcY), camera);
  return raycaster;
}
